import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { SearchInput, FilterBar, Drawer, useToast } from '@/components/base';
import {
  type CreateLegalProductInput,
  type LegalProduct,
  useLegalProducts,
} from '@/hooks/useLegalProducts';

type ProductFormState = {
  name: string;
  slug: string;
  category: string;
  description: string;
  targetAudience: string;
  baseFee: string;
  successFeePercentage: string;
  minFee: string;
  maxFee: string;
  installmentCount: string;
  isActive: boolean;
  notes: string;
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : currencyFormatter.format(value);

const formatPercentage = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value}%`;

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isInteger(number) ? number : null;
};

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyFormState: ProductFormState = {
  name: '',
  slug: '',
  category: '',
  description: '',
  targetAudience: '',
  baseFee: '',
  successFeePercentage: '',
  minFee: '',
  maxFee: '',
  installmentCount: '',
  isActive: true,
  notes: '',
};

const productToFormState = (product: LegalProduct): ProductFormState => ({
  name: product.name,
  slug: product.slug ?? '',
  category: product.category ?? '',
  description: product.description ?? '',
  targetAudience: product.targetAudience ?? '',
  baseFee: product.baseFee === null || product.baseFee === undefined ? '' : String(product.baseFee),
  successFeePercentage:
    product.successFeePercentage === null || product.successFeePercentage === undefined
      ? ''
      : String(product.successFeePercentage),
  minFee: product.minFee === null || product.minFee === undefined ? '' : String(product.minFee),
  maxFee: product.maxFee === null || product.maxFee === undefined ? '' : String(product.maxFee),
  installmentCount:
    product.installmentCount === null || product.installmentCount === undefined
      ? ''
      : String(product.installmentCount),
  isActive: product.isActive,
  notes: product.notes ?? '',
});

const ProductsPage: FC = () => {
  const { canManageLegalStructures } = useRolePermissions();
  const { addToast } = useToast();
  const {
    legalProducts,
    isLoadingLegalProducts,
    legalProductsError,
    loadLegalProducts,
    createLegalProduct,
    updateLegalProduct,
    deleteLegalProduct,
  } = useLegalProducts();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<LegalProduct | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [isSavingLegalProduct, setIsSavingLegalProduct] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormState>(emptyFormState);
  const [admissionTasks, setAdmissionTasks] = useState('');
  const [admissionChecklistIds, setAdmissionChecklistIds] = useState<string[]>([]);
  const [checklists, setChecklists] = useState<Array<{ id: string; name: string }>>([]);
  const [admissionTemplates, setAdmissionTemplates] = useState<Array<{ id: string; name: string }>>([]);
  const [admissionSaved, setAdmissionSaved] = useState(false);

  useEffect(() => {
    void fetch('/api/legal-checklists').then((response) => response.json()).then((data) => setChecklists(data.legalChecklists ?? data.checklists ?? [])).catch(() => setChecklists([]));
  }, []);

  useEffect(() => {
    if (!selectedProduct) return;
    setAdmissionSaved(false);
    void fetch(`/api/legal-products/${selectedProduct.id}/admission-package`).then((response) => response.json()).then((data) => {
      const pkg = data.package ?? {};
      setAdmissionTasks((pkg.tasks ?? []).map((task: any) => task.title).join('\n'));
      setAdmissionChecklistIds((pkg.checklists ?? []).map((item: any) => item.id));
      setAdmissionTemplates(pkg.templates ?? []);
    }).catch(() => { setAdmissionTasks(''); setAdmissionChecklistIds([]); setAdmissionTemplates([]); });
  }, [selectedProduct]);

  const saveAdmissionPackage = async () => {
    if (!selectedProduct) return;
    const response = await fetch(`/api/legal-products/${selectedProduct.id}/admission-package`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tasks: admissionTasks.split('\n').map((title) => ({ title: title.trim() })).filter((item) => item.title), checklistIds: admissionChecklistIds }) });
    if (!response.ok) throw new Error('Não foi possível salvar o pacote de admissão.');
    setAdmissionSaved(true);
  };

  const visibleProducts = useMemo(
    () => legalProducts.filter((product) => product.isActive),
    [legalProducts]
  );

  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleProducts
            .map((product) => product.category)
            .filter((category): category is string => Boolean(category))
        )
      )
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
        .map((category) => ({ label: category, value: category })),
    [visibleProducts]
  );

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return visibleProducts.filter((product) => {
      if (categoryFilter && product.category !== categoryFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [
        product.name,
        product.slug,
        product.category,
        product.description,
        product.targetAudience,
        product.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [categoryFilter, search, visibleProducts]);

  const openCreateForm = () => {
    setSelectedProduct(null);
    setEditingProductId(null);
    setForm(emptyFormState);
    setShowForm(true);
  };

  const openEditForm = (product: LegalProduct) => {
    setSelectedProduct(null);
    setEditingProductId(product.id);
    setForm(productToFormState(product));
    setShowForm(true);
  };

  const closeForm = () => {
    if (isSavingLegalProduct) {
      return;
    }

    setShowForm(false);
    setEditingProductId(null);
    setForm(emptyFormState);
  };

  const buildPayload = (): CreateLegalProductInput => {
    const name = form.name.trim();

    return {
      name,
      slug: toNullableText(form.slug) || slugify(name),
      category: toNullableText(form.category),
      description: toNullableText(form.description),
      targetAudience: toNullableText(form.targetAudience),
      baseFee: toNullableNumber(form.baseFee),
      successFeePercentage: toNullableNumber(form.successFeePercentage),
      minFee: toNullableNumber(form.minFee),
      maxFee: toNullableNumber(form.maxFee),
      installmentCount: toNullableInteger(form.installmentCount),
      isActive: form.isActive,
      notes: toNullableText(form.notes),
    };
  };

  const handleSave = async () => {
    if (isSavingLegalProduct) {
      return;
    }

    const name = form.name.trim();
    if (!name) {
      addToast('Preencha o nome do produto jurídico.', 'warning');
      return;
    }

    setIsSavingLegalProduct(true);

    try {
      const payload = buildPayload();

      if (editingProductId) {
        const updated = await updateLegalProduct(editingProductId, payload);
        addToast('Produto jurídico atualizado com sucesso.', 'success');

        if (selectedProduct?.id === updated.id) {
          if (updated.isActive) {
            setSelectedProduct(updated);
          } else {
            setSelectedProduct(null);
          }
        }
      } else {
        await createLegalProduct(payload);
        addToast('Produto jurídico criado com sucesso.', 'success');
      }

      setShowForm(false);
      setEditingProductId(null);
      setForm(emptyFormState);
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o produto jurídico.', 'error');
    } finally {
      setIsSavingLegalProduct(false);
    }
  };

  const handleDelete = async (product: LegalProduct) => {
    if (deletingProductId === product.id) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja excluir este produto jurídico? Esta ação remover o produto da listagem, mas manter o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingProductId(product.id);

    try {
      await deleteLegalProduct(product.id);
      addToast('Produto jurídico excluído com sucesso.', 'success');

      if (selectedProduct?.id === product.id) {
        setSelectedProduct(null);
      }

      if (editingProductId === product.id) {
        setShowForm(false);
        setEditingProductId(null);
        setForm(emptyFormState);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível excluir o produto jurídico.', 'error');
    } finally {
      setDeletingProductId(null);
    }
  };

  const actionButtonClassName =
    'px-2 py-1 rounded text-[10px] font-medium bg-background-100 text-foreground-600 transition-colors cursor-pointer whitespace-nowrap';

  const saveButtonClassName = `px-3 py-1.5 rounded-lg text-xs font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap ${
    isSavingLegalProduct ? 'bg-primary-300' : 'bg-primary-500 hover:bg-primary-600'
  }`;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">

        {!canManageLegalStructures ? (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
          </div>
        ) : null}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Produtos Jurídicos</h2>
            <p className="text-sm text-foreground-500 mt-0.5">Catálogo interno de serviços</p>
          </div>
          <button
            hidden={!canManageLegalStructures}
            onClick={openCreateForm}
            disabled={isLoadingLegalProducts}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line"></i> Novo Produto Jurídico
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar produto..." className="sm:w-64" />
          <FilterBar
            filters={[
              {
                key: 'category',
                label: 'Categoria',
                options: categoryOptions,
                value: categoryFilter,
                onChange: setCategoryFilter,
              },
            ]}
          />
        </div>

        {isLoadingLegalProducts && (
          <div className="rounded-lg border border-dashed border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-500">
            Carregando produtos jurídicos...
          </div>
        )}

        {legalProductsError && !isLoadingLegalProducts && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{legalProductsError}</span>
            <button
              onClick={() => void loadLegalProducts()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoadingLegalProducts && filteredProducts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-background-200 bg-background-50 p-8 text-center">
            <p className="text-sm font-medium text-foreground-700">Nenhum produto jurídico encontrado</p>
            <p className="text-xs text-foreground-500 mt-1">Ajuste os filtros ou cadastre um novo produto.</p>
          </div>
        ) : (
          !isLoadingLegalProducts && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className="bg-background-50 border border-background-200 rounded-xl p-5 group relative cursor-pointer hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-foreground-900 hover:text-primary-700 transition-colors truncate">
                        {product.name}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 truncate">
                        {product.category || 'Sem categoria'}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                        product.isActive
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-foreground-100 text-foreground-500 border border-foreground-200'
                      }`}
                    >
                      {product.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>

                  <p className="text-sm text-foreground-600 line-clamp-2 mb-4 min-h-[2.5rem]">
                    {product.description || 'Sem descrição informada.'}
                  </p>

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-background-100">
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Honorário base</p>
                      <p className="text-sm font-bold text-foreground-900">{formatCurrency(product.baseFee)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Êxito</p>
                      <p className="text-sm font-bold text-primary-700">
                        {formatPercentage(product.successFeePercentage)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-background-100 flex-wrap">
                    <button
            hidden={!canManageLegalStructures}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditForm(product);
                      }}
                      disabled={isSavingLegalProduct || deletingProductId === product.id}
                      className={`${actionButtonClassName} hover:bg-primary-50 hover:text-primary-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                      title="Editar"
                    >
                      <i className="ri-edit-line mr-0.5"></i>Editar
                    </button>
                    <button
            hidden={!canManageLegalStructures}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(product);
                      }}
                      disabled={isSavingLegalProduct || deletingProductId === product.id}
                      className={`${actionButtonClassName} hover:bg-red-50 hover:text-red-600 disabled:opacity-60 disabled:cursor-not-allowed`}
                      title="Excluir"
                    >
                      <i className="ri-delete-bin-line mr-0.5"></i>
                      {deletingProductId === product.id ? 'Excluindo...' : 'Excluir'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        <Drawer
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          title={selectedProduct ? selectedProduct.name : ''}
          width="w-[560px]"
        >
          {selectedProduct && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">{selectedProduct.category || 'Sem categoria'}</span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    selectedProduct.isActive
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-foreground-100 text-foreground-500 border border-foreground-200'
                  }`}
                >
                  {selectedProduct.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </div>

              <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">
                {selectedProduct.description || 'Sem descrição informada.'}
              </p>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Slug</p>
                  <p className="text-sm font-medium text-foreground-800 break-all">{selectedProduct.slug || '—'}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Público-alvo</p>
                  <p className="text-sm font-medium text-foreground-800">{selectedProduct.targetAudience || '—'}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Honorário base</p>
                  <p className="text-sm font-medium text-foreground-800">{formatCurrency(selectedProduct.baseFee)}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Percentual de êxito</p>
                  <p className="text-sm font-medium text-foreground-800">
                    {formatPercentage(selectedProduct.successFeePercentage)}
                  </p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Honorário mínimo</p>
                  <p className="text-sm font-medium text-foreground-800">{formatCurrency(selectedProduct.minFee)}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Honorário máximo</p>
                  <p className="text-sm font-medium text-foreground-800">{formatCurrency(selectedProduct.maxFee)}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Parcelas</p>
                  <p className="text-sm font-medium text-foreground-800">{selectedProduct.installmentCount ?? '—'}</p>
                </div>
                <div className="p-3 bg-background-100 rounded-lg">
                  <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Atualizado em</p>
                  <p className="text-sm font-medium text-foreground-800 break-all">{selectedProduct.updatedAt || '—'}</p>
                </div>
              </div>

              {selectedProduct.notes && (
                <div className="p-3 bg-amber-50/50 border border-amber-100 rounded-lg">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Observações</p>
                  <p className="text-sm text-amber-800">{selectedProduct.notes}</p>
                </div>
              )}

              <div className="space-y-3 border-t border-background-200 pt-4">
                <div>
                  <p className="text-sm font-semibold text-foreground-800">Pacote de admissão</p>
                  <p className="text-xs text-foreground-500">Defina os itens que serão preparados no Caso, usando os módulos existentes.</p>
                </div>
                <label className="block text-xs text-foreground-600">Tarefas iniciais (uma por linha)
                  <textarea value={admissionTasks} onChange={(event) => setAdmissionTasks(event.target.value)} disabled={!canManageLegalStructures} rows={3} className="mt-1 w-full rounded-lg border border-background-200 p-2 text-sm" placeholder="Conferir documentação\nRevisar contrato" />
                </label>
                <div className="space-y-1">
                  <p className="text-xs text-foreground-600">Checklists padrão</p>
                  {checklists.map((checklist) => <label key={checklist.id} className="flex items-center gap-2 text-xs text-foreground-700"><input type="checkbox" checked={admissionChecklistIds.includes(checklist.id)} disabled={!canManageLegalStructures} onChange={(event) => setAdmissionChecklistIds((current) => event.target.checked ? [...current, checklist.id] : current.filter((id) => id !== checklist.id))} />{checklist.name}</label>)}
                  {checklists.length === 0 && <p className="text-xs text-foreground-400">Nenhum checklist disponível.</p>}
                </div>
                <div className="text-xs text-foreground-600">Modelos associados ao Produto: {admissionTemplates.length ? admissionTemplates.map((template) => template.name).join(', ') : 'nenhum'}</div>
                {canManageLegalStructures && <button onClick={() => void saveAdmissionPackage().then(() => addToast('Pacote de admissão salvo.', 'success')).catch((error: any) => addToast(error?.message || 'Falha ao salvar pacote.', 'error'))} className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs">Salvar pacote</button>}
                {admissionSaved && <span className="text-xs text-emerald-700">Salvo.</span>}
              </div>

              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-background-200">
                <button
            hidden={!canManageLegalStructures}
                  onClick={() => openEditForm(selectedProduct)}
                  disabled={isSavingLegalProduct || deletingProductId === selectedProduct.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-edit-line mr-1"></i>Editar
                </button>
                <button
            hidden={!canManageLegalStructures}
                  onClick={() => void handleDelete(selectedProduct)}
                  disabled={isSavingLegalProduct || deletingProductId === selectedProduct.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  {deletingProductId === selectedProduct.id ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={canManageLegalStructures && showForm}
          onClose={closeForm}
          title={editingProductId ? 'Editar Produto Jurídico' : 'Novo Produto Jurídico'}
          width="w-[640px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeForm}
                disabled={isSavingLegalProduct}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
            hidden={!canManageLegalStructures} onClick={handleSave} disabled={isSavingLegalProduct} className={saveButtonClassName}>
                {isSavingLegalProduct ? 'Salvando...' : 'Salvar Produto'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Nome *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: Fraude Pix"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="fraude-pix"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Categoria</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Direito do Consumidor"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Público-alvo</label>
                <input
                  type="text"
                  value={form.targetAudience}
                  onChange={(e) => setForm((prev) => ({ ...prev, targetAudience: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Consumidores com fraude bancária"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Descrição resumida do produto jurídico"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Honorário base (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.baseFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, baseFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="8500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Percentual de êxito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.successFeePercentage}
                  onChange={(e) => setForm((prev) => ({ ...prev, successFeePercentage: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="20"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Honorário mínimo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.minFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, minFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="5000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Honorário máximo (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.maxFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, maxFee: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="12000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Parcelas padrão</label>
                <input
                  type="number"
                  step="1"
                  value={form.installmentCount}
                  onChange={(e) => setForm((prev) => ({ ...prev, installmentCount: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="3"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 w-full">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 rounded border-background-300 text-primary-600 focus:ring-primary-500"
                  />
                  Produto ativo
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Anotações internas sobre o produto"
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default ProductsPage;
